import React, { useEffect, useRef, useState } from "react";
import { storeCachedImage } from "../helpers/imageCache";

export default function Search({
  onNftBackground,
  onNftAsset,
  onRemoveBackground,
  hasBackground,
  onLocalBgFile,
}) {
  // 1) State for UI / input control
  const isMobile = window.innerWidth <= 768;
  const [mode, setMode] = useState("background");
  const [walletAddress, setWalletAddress] = useState("");
  const [sealId, setSealId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // 2) Data states
  const [nftItems, setNftItems] = useState([]);
  const [memeItems, setMemeItems] = useState([]);
  const [memesCache, setMemesCache] = useState({});
  const [nftsCache, setNftsCache] = useState({});
  const [fetchedWallets, setFetchedWallets] = useState({});
  const [fetchedSeals, setFetchedSeals] = useState({});
  const fullImageCacheRef = useRef({});

  // 3) UI states
  const [loading, setLoading] = useState(false);
  const [walletError, setWalletError] = useState("");
  const [memesError, setMemesError] = useState("");
  const [templatesError, setTemplatesError] = useState("");

  // [CHANGE] We'll keep `imagesLoading` for NFTs or templates if you want,
  // but for memes specifically, we'll no longer do big preloads.
  const [imagesLoading, setImagesLoading] = useState(false);

  const [displayLimit, setDisplayLimit] = useState(50);

  const [templateItems, setTemplateItems] = useState([]);
  const [templatesCache, setTemplatesCache] = useState({});
  const [containerHeight, setContainerHeight] = useState("max-h-[400px]");
  // Remove spin on numeric inputs
  const noSpinCSS = `
    .noSpin::-webkit-inner-spin-button,
    .noSpin::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `;

  // Loading Spinner
  const Spinner = () => {
    return (
      <div
        className="
          inline-block
          h-4 w-4
          border-2 border-monadAccent border-solid
          rounded-full
          border-t-transparent
          animate-spin
        "
        role="status"
      >
        <span className="sr-only">Loading...</span>
      </div>
    );
  };

  // =============================
  // 4) Load caches from sessionStorage on mount
  // =============================
  useEffect(() => {
    const storedMemes = sessionStorage.getItem("memesCache");
    if (storedMemes) {
      try {
        setMemesCache(JSON.parse(storedMemes));
      } catch {}
    }
    const storedNfts = sessionStorage.getItem("nftsCache");
    if (storedNfts) {
      try {
        setNftsCache(JSON.parse(storedNfts));
      } catch {}
    }
    const storedTemplates = sessionStorage.getItem("templatesCache");
    if (storedTemplates) {
      try {
        setTemplatesCache(JSON.parse(storedTemplates));
      } catch {}
    }
  }, []);

  // =============================
  // 5) Load UI state from sessionStorage on mount
  // =============================
  useEffect(() => {
    const savedUIState = sessionStorage.getItem("searchTabUIState");
    if (savedUIState) {
      try {
        const parsed = JSON.parse(savedUIState);
        setMode(parsed.mode ?? "wallet");
        setWalletAddress(parsed.walletAddress ?? "");
        setSealId(parsed.sealId ?? "");
        setSearchQuery(parsed.searchQuery ?? "");
      } catch {}
    }
  }, []);

  // =============================
  // 6) Re-hydrate items from cache if we already had them
  // =============================
  useEffect(() => {
    if (mode === "wallet" && walletAddress.trim()) {
      const addressKey = walletAddress.trim().toLowerCase();
      if (nftsCache[addressKey]) {
        setNftItems(nftsCache[addressKey]);
      }
    }
    if (mode === "memes" && sealId) {
      if (memesCache[sealId]) {
        setMemeItems(memesCache[sealId]);
      }
    }
    if (mode === "templates") {
      if (templatesCache["popular"]) {
        setTemplateItems(templatesCache["popular"]);
      }
    }
  }, [mode, walletAddress, sealId, nftsCache, memesCache, templatesCache]);

  // =============================
  // 7) Persist caches
  // =============================
  useEffect(() => {
    sessionStorage.setItem("memesCache", JSON.stringify(memesCache));
  }, [memesCache]);

  useEffect(() => {
    sessionStorage.setItem("nftsCache", JSON.stringify(nftsCache));
  }, [nftsCache]);

  useEffect(() => {
    sessionStorage.setItem("templatesCache", JSON.stringify(templatesCache));
  }, [templatesCache]);

  // =============================
  // 8) Persist UI
  // =============================
  useEffect(() => {
    const uiState = {
      mode,
      walletAddress,
      sealId,
      searchQuery,
    };
    sessionStorage.setItem("searchTabUIState", JSON.stringify(uiState));
  }, [mode, walletAddress, sealId, searchQuery]);

  // =============================
  // 10) Preload images (still used for NFTs or templates if you want)
  // =============================
  const preloadFullImages = (items) => {
    if (!items || items.length === 0) return;
    items.forEach((item) => {
      const fullUrl = item.image;
      if (!fullUrl) return;

      // If not already cached, load once:
      if (!fullImageCacheRef.current[fullUrl]) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = fullUrl;
        img.onload = () => {
          storeCachedImage(fullUrl, img);
        };
      }
    });
  };

  const preloadThumbnails = (items) => {
    return new Promise((resolve) => {
      if (!items || items.length === 0) {
        resolve([]);
        return;
      }

      let loadedCount = 0;
      const total = items.length;
      const validItems = [];

      items.forEach((item) => {
        const ipfsUrl = item?.media?.[0]?.gateway;
        if (!ipfsUrl) {
          loadedCount++;
          if (loadedCount >= total) resolve(validItems);
          return;
        }

        const img = new Image();
        img.src = ipfsUrl;
        img.onload = () => {
          validItems.push(item);
          loadedCount++;
          if (loadedCount >= total) resolve(validItems);
        };
        img.onerror = () => {
          loadedCount++;
          if (loadedCount >= total) resolve(validItems);
        };
      });
    });
  };

  // =============================
  // 11) Fetch NFTs
  // =============================
  const fetchNFTs = async () => {
    try {
      setLoading(true);
      setWalletError("");
      setImagesLoading(false);
      setDisplayLimit(20);

      const addressKey = walletAddress.trim().toLowerCase();
      if (nftsCache[addressKey]) {
        // Use cache
        setImagesLoading(true);
        await preloadThumbnails(nftsCache[addressKey]);
        setNftItems(nftsCache[addressKey]);
        setImagesLoading(false);
        setFetchedWallets((prev) => ({ ...prev, [addressKey]: true }));
        setLoading(false);
        return nftsCache[addressKey];
      }

      const backendBase =
        "https://qs10n5ia26.execute-api.us-east-2.amazonaws.com/prod";
      const fetchURL = `${backendBase}/getNFTs?owner=${walletAddress}`;
      const response = await fetch(fetchURL);
      if (!response.ok) {
        throw new Error("Failed to fetch NFTs. Check wallet address or API key.");
      }

      const data = await response.json();
      if (data?.ownedNfts?.length > 0) {
        setImagesLoading(true);
        const validItems = await preloadThumbnails(data.ownedNfts);
        setNftItems(validItems);
        setNftsCache((prev) => ({
          ...prev,
          [addressKey]: validItems,
        }));
        setFetchedWallets((prev) => ({ ...prev, [addressKey]: true }));
        setImagesLoading(false);
      } else {
        setWalletError("No NFTs found or invalid wallet address.");
      }
      return data.ownedNfts || [];
    } catch (err) {
      setWalletError(err.message || "An error occurred while fetching NFTs.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleFetchNFTs = async () => {
    try {
      const data = await fetchNFTs(walletAddress);
      setNftItems(data);
    } catch (err) {
      console.error(err);
    }
  };

  // =============================
  // 12) Fetch Memes
  // =============================
  const fetchMemes = async (inputSealId) => {
    try {
      setLoading(true);
      setMemesError("");
      // [CHANGE] We won't do big thumbnail preloads here
      setImagesLoading(false);
      setDisplayLimit(16);

      // If we already have this seal's data cached, use it
      if (memesCache[sealId]) {
        setMemeItems(memesCache[sealId]);
        setFetchedSeals((prev) => ({ ...prev, [inputSealId]: true }));
        setLoading(false);
        return memesCache[inputSealId];
      }

      const fetchURL = `https://s8pbvex589.execute-api.us-east-2.amazonaws.com/prod/${sealId}`;
      const response = await fetch(fetchURL);
      if (!response.ok) {
        throw new Error("Failed to fetch from meme machine.");
      }

      const data = await response.json();

      // Flatten
      const flattened = [];
      data.forEach((item) => {
        flattened.push(item);
        if (Array.isArray(item.children)) {
          item.children.forEach((child) => flattened.push(child));
        }
      });

      if (!flattened.length) {
        setMemesError("No arfs found or the API returned an unexpected format.");
        return;
      }

      // [CHANGE] Instead of preloading all images (or the first 50),
      // just store them in state. We'll rely on actual <img loading="lazy" />.
      setMemeItems(flattened);
      setMemesCache((prev) => ({ ...prev, [sealId]: flattened }));
      setFetchedSeals((prev) => ({ ...prev, [inputSealId]: true }));

      return flattened;
    } catch (err) {
      setMemesError(err.message || "An error occurred while fetching memes.");
    } finally {
      setLoading(false);
    }
  };

  const handleFetchMemes = async () => {
    try {
      const data = await fetchMemes(sealId);
      setMemeItems(data);
    } catch (err) {
      console.error(err);
    }
  };

  // =============================
  // 12b) Fetch ImgFlip
  // =============================
  const fetchImgFlipMemes = async () => {
    try {
      setLoading(true);
      setTemplatesError("");
      setImagesLoading(false);
      setDisplayLimit(50);

      if (templatesCache["popular"]) {
        setImagesLoading(true);
        await preloadThumbnails(templatesCache["popular"]);
        setTemplateItems(templatesCache["popular"]);
        setImagesLoading(false);
        setLoading(false);
        return;
      }

      const response = await fetch("https://api.imgflip.com/get_memes");
      if (!response.ok) {
        throw new Error("Failed to fetch Meme Templates from ImgFlip.");
      }
      const data = await response.json();
      if (data?.success && data?.data?.memes?.length) {
        const memesArray = data.data.memes;
        setImagesLoading(true);
        await preloadThumbnails(memesArray);
        setTemplateItems(memesArray);

        setTemplatesCache((prev) => ({
          ...prev,
          popular: memesArray,
        }));
        setImagesLoading(false);
      } else {
        setTemplatesError("No meme templates returned from ImgFlip API.");
      }
    } catch (err) {
      setTemplatesError(
        err.message || "An error occurred while fetching meme templates."
      );
    } finally {
      setLoading(false);
    }
  };

  // =============================
  // 13) Filter Helpers
  // =============================
  const getFilteredMemes = () => {
    if (!searchQuery.trim()) {
      return memeItems;
    }
    const q = searchQuery.toLowerCase();
    return memeItems.filter((meme) => {
      const t = (meme.display_name || meme.name || "").toLowerCase();
      const a = (meme.author || "").toLowerCase();
      return t.includes(q) || a.includes(q);
    });
  };

  const getFilteredTemplates = () => {
    if (!searchQuery.trim()) {
      return templateItems;
    }
    const q = searchQuery.toLowerCase();
    return templateItems.filter((tpl) => {
      const name = (tpl.name || "").toLowerCase();
      return name.includes(q);
    });
  };

  const getFilteredNfts = () => {
    if (!searchQuery.trim()) {
      return nftItems;
    }
    const q = searchQuery.toLowerCase();
    return nftItems.filter((nft) => {
      const t = (nft.title || "").toLowerCase();
      return t.includes(q);
    });
  };

  let displayedAll = [];
  if (mode === "templates") {
    displayedAll = getFilteredTemplates();
  } else if (mode === "wallet") {
    displayedAll = getFilteredNfts();
  } else if (mode === "memes") {
    displayedAll = getFilteredMemes();
  }

  const displayedSubset = displayedAll.slice(0, displayLimit);
  const canLoadMore = displayedAll.length > displayLimit;

  // [CHANGE] We still can preload NFT or template images here if you want,
  // but we won't do it for memes. So let's keep the effect as-is for NFTs/templates.
  useEffect(() => {
    if (!imagesLoading && displayedSubset.length > 0) {
      // Only do the fullImage preload for wallets or templates if you want.
      // For memes, we skip:
      if (mode !== "memes") {
        preloadFullImages(displayedSubset);
      }
    }
  }, [imagesLoading, displayedSubset, mode]);

  // Determine container height on mount/resizing
  useEffect(() => {
    function updateHeight() {
      const w = window.innerWidth;
      if (w <= 768) {
        setContainerHeight("max-h-[300px]");
      } else if (w <= 1600) {
        setContainerHeight("max-h-[300px]");
      } else {
        setContainerHeight("max-h-[500px]");
      }
    }
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Additional helpers
  const sealIdIsValid = () => {
    if (!sealId) return false;
    const num = parseInt(sealId, 10);
    return num >= 22 && num <= 10000;
  };

  function handleSetSolidColor(colorHex, onLocalBgFile) {
    const size = 512;
    const solidCanvas = document.createElement("canvas");
    solidCanvas.width = size;
    solidCanvas.height = size;
    const ctx = solidCanvas.getContext("2d");

    ctx.fillStyle = colorHex;
    ctx.fillRect(0, 0, size, size);

    const dataUrl = solidCanvas.toDataURL("image/png");
    fetch(dataUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "solidColor.png", { type: "image/png" });
        onLocalBgFile(file);
      });
  }

  const addressKey = walletAddress.trim().toLowerCase();
  const alreadyFetchedWallet = !!fetchedWallets[addressKey];
  const alreadyFetchedSeal = !!fetchedSeals[sealId];
  const popularMemesLoaded =
    Array.isArray(templatesCache["popular"]) && templatesCache["popular"].length > 0;

  return (
    <div className="text-monadWhite flex flex-col items-start w-full">
      <style>{noSpinCSS}</style>

      {/* Mode Selector */}
      <div className="flex items-center justify-start gap-2 mb-4">
        <label htmlFor="modeSelect" className="whitespace-nowrap text-sm md:text-lg">
          Mode:
        </label>
        <select
          id="modeSelect"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          className="px-2 py-1 rounded bg-monadDark text-white border border-monadGray w-64 text-sm md:text-lg"
        >
          <option value="background">Upload Background</option>
          <option value="solidcolor">Choose Color</option>
          <option value="templates">ImgFlip Meme Templates</option>
          <option value="wallet">Load NFTs from Wallet</option>
          <option value="memes">Sappy Seal Meme Machine</option>
        </select>
      </div>

      {/* ===================================================== */}
      {/* ================ BACKGROUND UPLOAD =================== */}
      {/* ===================================================== */}
      {mode === "background" && (
        <div className="flex flex-col items-start w-full">
          <input
            id="bgFileInput"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                onLocalBgFile(file);
              }
            }}
          />
          <div className="flex items-center justify-start gap-2 mb-4">
            <button
              onClick={() => {
                document.getElementById("bgFileInput")?.click();
              }}
              className="px-3 py-1 rounded bg-monadAccent text-white hover:bg-monadAccentDark text-sm md:text-base"
            >
              Upload
            </button>
            <button
              onClick={onRemoveBackground}
              disabled={!hasBackground}
              className="
                px-3 py-1 rounded bg-red-600 text-white
                hover:bg-monadAccentDark text-sm md:text-base
                disabled:opacity-60 disabled:cursor-not-allowed
              "
            >
              Delete Background
            </button>
          </div>
        </div>
      )}

      {/* ===================================================== */}
      {/* ================ Solid Color ======================== */}
      {/* ===================================================== */}
      {mode === "solidcolor" && (
        <div className="flex flex-col items-start w-full">
          <p className="mb-2 text-sm md:text-base font-semibold">
            Pick a solid background color:
          </p>
          <div className="flex items-center gap-2">
            <input
              type="color"
              defaultValue="#836EF9"
              onChange={(e) => {
                const chosenColor = e.target.value;
                handleSetSolidColor(chosenColor, onLocalBgFile);
              }}
              className="w-16 h-8 cursor-pointer border border-monadGray rounded"
            />
            <button
              onClick={onRemoveBackground}
              disabled={!hasBackground}
              className="
                px-3 py-1 rounded bg-red-600 text-white
                hover:bg-monadAccentDark text-sm md:text-base
                disabled:opacity-60 disabled:cursor-not-allowed
              "
            >
              Delete Background
            </button>
          </div>
        </div>
      )}

      {/* ===================================================== */}
      {/* ================ TEMPLATES (ImgFlip) ================ */}
      {/* ===================================================== */}
      {mode === "templates" && (
        <div className="flex flex-col items-start w-full">
          <div className="flex items-center justify-start gap-2 mb-4">
            <button
              onClick={fetchImgFlipMemes}
              disabled={loading || popularMemesLoaded}
              className={`
                px-3 py-1 rounded text-white text-sm md:text-base
                ${
                  loading || popularMemesLoaded
                    ? "bg-monadGray cursor-not-allowed"
                    : "bg-monadAccent hover:bg-monadAccentDark cursor-pointer"
                }
              `}
            >
              {loading ? "Loading..." : popularMemesLoaded ? "Memes Loaded" : "Load Memes"}
            </button>
            <button
              onClick={onRemoveBackground}
              disabled={!hasBackground}
              className={`
                px-3 py-1 rounded bg-red-600 text-white text-sm md:text-base hover:bg-monadAccentDark
                disabled:opacity-60 disabled:cursor-not-allowed
              `}
            >
              Delete Background
            </button>
          </div>

          {/* Filter input */}
          <div className="flex items-center justify-start gap-2 mb-4">
            <label
              htmlFor="searchQueryTemplates"
              className="whitespace-nowrap text-sm md:text-base"
            >
              Filter:
            </label>
            <input
              id="searchQueryTemplates"
              type="text"
              placeholder="Search template name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-2 py-1 rounded bg-monadDark text-white border border-monadGray w-56 md:w-64 text-sm md:text-base"
            />
          </div>

          {templatesError && <p className="text-red-500 mb-4">{templatesError}</p>}
          {imagesLoading && (
            <div className="flex items-center gap-2 mb-2 text-yellow-400">
              <Spinner />
              <span>Loading...</span>
            </div>
          )}

          {/* Show More on Mobile */}
          {isMobile && canLoadMore && (
            <button
              onClick={() => setDisplayLimit((prev) => prev + 50)}
              className="mb-3 px-3 py-1 rounded bg-monadAccent text-white text-sm md:text-base"
            >
              Show More
            </button>
          )}

          {/* Templates Grid */}
          {!imagesLoading && displayedSubset.length > 0 && (
            <div className={`grid grid-cols-2 gap-2 ${containerHeight} overflow-y-auto w-full`}>
              {displayedSubset.map((tpl, idx) => {
                const { url, name } = tpl;
                const thumb = url || "/images/assets/placeholder.png";
                const title = name || `Meme ${idx + 1}`;
                return (
                  <div
                    key={tpl.id || idx}
                    className="flex flex-col items-center border border-monadGray rounded p-2"
                  >
                    <img
                      src={thumb}
                      alt={title}
                      className="w-32 h-auto object-contain object-center mb-2 mx-auto"
                    />
                    <p className="text-sm md:text-base mb-2 max-w-[120px] break-words text-center">
                      {title}
                    </p>
                    {/* Only Add as Background */}
                    <button
                      onClick={() => {
                        if (onNftBackground) {
                          onNftBackground({ memeUrl: url });
                        }
                      }}
                      className="px-3 py-1 rounded bg-monadAccent text-white text-xs md:text-base hover:bg-monadAccentDark mt-auto"
                    >
                      Set Background
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Show More on Desktop */}
          {!isMobile && canLoadMore && (
            <button
              onClick={() => setDisplayLimit((prev) => prev + 50)}
              className="mt-3 px-3 py-1 rounded bg-monadAccent text-white text-sm md:text-base"
            >
              Show More
            </button>
          )}
        </div>
      )}

      {/* ===================================================== */}
      {/* ================== WALLET NFT SEARCH ================= */}
      {/* ===================================================== */}
      {mode === "wallet" && (
        <div className="flex flex-col items-start w-full">
          {/* Network selection */}
          <div className="flex items-center justify-start gap-2 mb-4">
            <label htmlFor="networkSelect" className="whitespace-nowrap text-sm md:text-base">
              Network:
            </label>
            <select
              id="networkSelect"
              className="px-2 py-1 rounded bg-monadDark text-white text-sm md:text-base border border-monadGray w-56 md:w-64"
              disabled
            >
              <option value="ethereum">Ethereum</option>
            </select>
          </div>

          {/* Wallet address */}
          <div className="flex items-center justify-start gap-2 mb-4">
            <label htmlFor="walletInput" className="whitespace-nowrap text-sm md:text-base">
              Wallet:
            </label>
            <input
              id="walletInput"
              type="text"
              placeholder="0x427A0...0dd64"
              onChange={(e) => {
                setWalletAddress(e.target.value);
                setWalletError("");
              }}
              value={walletAddress}
              className="px-2 py-1 rounded bg-monadDark text-white border text-sm md:text-base border-monadGray w-32 md:w-40"
            />
            <button
              onClick={handleFetchNFTs}
              disabled={!walletAddress || loading || alreadyFetchedWallet}
              className={`
                px-3 py-1 rounded text-white text-sm md:text-base
                ${
                  !walletAddress || loading || alreadyFetchedWallet
                    ? "bg-monadGray cursor-not-allowed"
                    : "bg-monadAccent hover:bg-monadAccentDark"
                }
              `}
            >
              {loading ? "Loading..." : alreadyFetchedWallet ? "Loaded" : "Load NFTs"}
            </button>
          </div>

          {/* Filter input */}
          <div className="flex items-center justify-start gap-2 mb-4">
            <label htmlFor="searchQueryWallet" className="whitespace-nowrap text-sm md:text-base">
              Filter:
            </label>
            <input
              id="searchQueryWallet"
              type="text"
              placeholder="Search by NFT Title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-2 py-1 rounded bg-monadDark text-white text-sm md:text-base border border-monadGray w-56 md:w-64"
            />
          </div>

          <button
            onClick={onRemoveBackground}
            disabled={!hasBackground}
            className={`
              px-3 py-1 mb-4 rounded bg-red-600 text-white text-sm md:text-base hover:bg-monadAccentDark
              disabled:opacity-60 disabled:cursor-not-allowed
            `}
          >
            Delete Background
          </button>

          {isMobile && canLoadMore && (
            <button
              onClick={() => setDisplayLimit((prev) => prev + 50)}
              className="mb-2 px-3 py-1 rounded bg-monadAccent text-white text-sm md:text-base"
            >
              Show More
            </button>
          )}

          {walletError && <p className="text-red-500 mb-4">{walletError}</p>}
          {imagesLoading && (
            <div className="flex items-center gap-2 mb-2 text-yellow-400">
              <Spinner />
              <span>Loading NFTs...</span>
            </div>
          )}

          {!imagesLoading && displayedSubset.length > 0 && (
            <div className={`grid grid-cols-2 gap-4 ${containerHeight} overflow-y-auto w-full`}>
              {displayedSubset.map((nft, idx) => {
                const contractAddress = nft?.contract?.address;
                const tokenIdHex = nft?.id?.tokenId;
                const { title, media } = nft;
                const fallbackImage =
                  media?.[0]?.gateway || "/images/assets/placeholder.png";

                return (
                  <div
                    key={idx}
                    className="flex flex-col items-center border border-monadGray rounded p-2"
                  >
                    <img
                      src={fallbackImage}
                      alt={title || "NFT"}
                      className="w-32 h-auto object-contain object-center mb-2 mx-auto"
                    />
                    <p className="text-sm md:text-base mb-2 max-w-[120px] truncate whitespace-normal break-words">
                      {title || `NFT #${idx + 1}`}
                    </p>
                    <div className="flex flex-col gap-1 mt-auto w-full">
                      <button
                        onClick={() => {
                          onNftBackground?.({ contractAddress, tokenIdHex });
                        }}
                        className="px-3 py-1 rounded bg-monadAccent text-white text-xs md:text-base hover:bg-monadAccentDark w-full"
                      >
                        Set Background
                      </button>
                      <button
                        onClick={() => {
                          onNftAsset?.({ contractAddress, tokenIdHex });
                        }}
                        className="px-3 py-1 rounded bg-monadAccent text-white text-xs md:text-base hover:bg-monadAccentDark w-full"
                      >
                        Add Asset
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!isMobile && canLoadMore && (
            <button
              onClick={() => setDisplayLimit((prev) => prev + 50)}
              className="mt-3 px-3 py-1 rounded bg-monadAccent text-white text-sm md:text-base"
            >
              Show More
            </button>
          )}
        </div>
      )}

      {/* ===================================================== */}
      {/* ================ SAPPY SEAL MEME MACHINE ============ */}
      {/* ===================================================== */}
      {mode === "memes" && (
        <div className="flex flex-col items-start w-full">
          <div className="flex items-center justify-start gap-2 mb-4">
            <label htmlFor="sealId" className="whitespace-nowrap text-sm md:text-base">
              Seal Id:
            </label>
            <input
              id="sealId"
              type="number"
              min="1"
              max="10000"
              step="1"
              placeholder="1-10000"
              value={sealId}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setMemesError(""); // clear old error
                if (Number.isNaN(val)) {
                  setSealId("");
                } else if (val < 1) {
                  setSealId("1");
                } else if (val > 10000) {
                  setSealId("10000");
                } else {
                  setSealId(val.toString());
                }
                if (val >= 1 && val <= 21) {
                  setMemesError("No memes for Seal IDs 1-21.");
                }
              }}
              className="px-2 py-1 rounded bg-monadDark text-white text-sm md:text-base border border-monadGray w-20 noSpin"
            />

            <button
              onClick={handleFetchMemes}
              disabled={loading || !sealIdIsValid() || alreadyFetchedSeal}
              className={`
                px-3 py-1 rounded text-white text-sm md:text-base
                ${
                  loading || !sealIdIsValid() || alreadyFetchedSeal
                    ? "bg-monadGray cursor-not-allowed"
                    : "bg-monadAccent hover:bg-monadAccentDark"
                }
              `}
            >
              {loading ? "Loading..." : alreadyFetchedSeal ? "Arfs Loaded" : "Load Arfs"}
            </button>
          </div>

          {/* Filter input */}
          <div className="flex items-center justify-start gap-2 mb-4">
            <label htmlFor="searchQuery" className="whitespace-nowrap text-sm md:text-base">
              Filter:
            </label>
            <input
              id="searchQuery"
              type="text"
              placeholder="Search by Title/Author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-2 py-1 rounded bg-monadDark text-white border text-sm md:text-base border-monadGray w-56 md:w-64"
            />
          </div>

          <button
            onClick={onRemoveBackground}
            disabled={!hasBackground}
            className={`
              px-3 py-1 mb-3 rounded bg-red-600 text-white text-sm md:text-base hover:bg-monadAccentDark
              disabled:opacity-60 disabled:cursor-not-allowed
            `}
          >
            Delete Background
          </button>

          {memesError && (
            <p className="text-red-500 text-sm md:text-base mb-4">{memesError}</p>
          )}

          {/* [CHANGE] We no longer do big preloading => no "imagesLoading" spinner for memes */}
          {/* If you still want a spinner for the fetch, you can show it via `loading`. */}
          {loading && (
            <div className="flex items-center gap-2 mb-2 text-yellow-400">
              <Spinner />
              <span>Fetching Arfs...</span>
            </div>
          )}

          {isMobile && canLoadMore && (
            <button
              onClick={() => setDisplayLimit((prev) => prev + 40)}
              className="my-3 px-3 py-1 rounded bg-monadAccent text-white text-sm md:text-base"
            >
              Show More
            </button>
          )}

          {/** Meme grid **/}
          {/* We rely on <img loading="lazy"> for each item */}
          {displayedSubset.length > 0 && (
            <div className={`grid grid-cols-2 gap-4 ${containerHeight} overflow-y-auto w-full`}>
              {displayedSubset.map((meme, idx) => {
                const thumb = meme.thumbnail || "/images/assets/placeholder.png";
                const imageUrl = meme.image;
                const title = meme.display_name || meme.name || `Meme #${idx + 1}`;
                const { author } = meme;

                return (
                  <div
                    key={meme.id || idx}
                    className="flex flex-col items-center border border-monadGray rounded p-2"
                  >
                    {/* [CHANGE] We add loading="lazy" here! */}
                    <img
                      src={thumb}
                      alt={title}
                      loading="lazy"
                      className="w-32 h-32 object-cover object-center mb-2 mx-auto"
                    />
                    <p className="text-sm mb-1 max-w-[120px] truncate">{title}</p>
                    {author && (
                      <p className="text-xs text-gray-400 mb-2">Author: {author}</p>
                    )}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => onNftBackground?.({ memeUrl: imageUrl })}
                        className="px-3 py-1 rounded bg-monadAccent text-white text-xs md:text-base hover:bg-monadAccentDark"
                      >
                        Set Background
                      </button>
                      <button
                        onClick={() => onNftAsset?.({ memeUrl: imageUrl })}
                        className="px-3 py-1 rounded bg-monadAccent text-white text-xs md:text-base hover:bg-monadAccentDark"
                      >
                        Add Asset
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!isMobile && canLoadMore && (
            <button
              onClick={() => setDisplayLimit((prev) => prev + 40)}
              className="mt-3 px-3 py-1 rounded bg-monadAccent text-white"
            >
              Show More
            </button>
          )}
        </div>
      )}
    </div>
  );
}
